/**
 * @file: PatentsTable.js
 * @status: Sovereign IP Management Enabled (V12.3.0) - SECURITY SYNC ACTIVE
 * @seal: @CEO - Abdullah Ahmed Al-Zahrani
 * @description: إدارة الأصول المعرفية مع قفل صلاحيات التعميد للمستثمرين والربط السحابي المباشر.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, PlusCircle, Loader2, Zap } from 'lucide-react';
import { message } from 'antd';

const PatentsTable = ({ onApprove }) => {
    const [patents, setPatents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    
    // استدعاء الصلاحية والبيانات الأساسية
    const userRole = localStorage.getItem('huger_user_role');
    const API_URL = "https://portal.huger.sa/api.php";

    const [newPatent, setNewPatent] = useState({
        patent_id: '',
        title_ar: '',
        field: 'STEAM',
        protection_type: 'Patent'
    });

    const fetchPatents = () => {
        setLoading(true);
        axios.get(`${API_URL}?action=get_patents&nocache=${Date.now()}`)
            .then(res => { 
                const data = Array.isArray(res.data) ? res.data : [];
                setPatents(data); 
                setLoading(false); 
            })
            .catch(err => { 
                console.error("عائق في استدعاء السجل السحابي:", err); 
                setLoading(false); 
            });
    };

    useEffect(() => { fetchPatents(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        // تأمين إضافي: منع الإرسال إذا لم يكن المستخدم CEO
        if (userRole !== 'CEO') {
            message.error("صلاحية التعميد محصورة لسعادة الرئيس فقط.");
            return;
        }

        const hide = message.loading("جاري المزامنة السيادية...", 0);
        
        // استخدام URLSearchParams لضمان أقصى درجات التوافق مع المحرك PHP V9.1.0
        const params = new URLSearchParams();
        params.append('patent_id', newPatent.patent_id);
        params.append('title_ar', newPatent.title_ar);
        params.append('field', newPatent.field);
        params.append('protection_type', newPatent.protection_type);

        try {
            const response = await axios.post(`${API_URL}?action=add_patent`, params);
            if (response.data && response.data.success) {
                setShowForm(false);
                fetchPatents();
                message.success("تم تعميد الابتكار في سجلات ضخامة بنجاح @CEO ✅");
                setNewPatent({ patent_id: '', title_ar: '', field: 'STEAM', protection_type: 'Patent' });
            } else {
                message.error(response.data.message || "فشل التعميد السحابي");
            }
        } catch (err) { 
            message.error("عائق تقني في الاتصال بالمحرك السحابي"); 
        } finally {
            hide();
        }
    };

    if (loading && patents.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-slate-500">
                <Loader2 className="animate-spin mb-4 text-[#F57C00]" size={24} />
                <p className="text-[10px] font-black uppercase tracking-widest italic">مزامنة الأصول السيادية...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* الشريط العلوي مع فلتر الصلاحيات البصري */}
            <div className="flex justify-between items-center px-4">
                <h3 className="text-sm font-black italic text-white flex items-center gap-3 uppercase tracking-tighter">
                    <ShieldAlert className="text-[#F57C00]" size={18} /> محفظة الأصول ({patents.length})
                </h3>
                
                {/* قفل الصلاحيات: زر الإضافة يختفي تماماً للمستثمر والموظف */}
                {userRole === 'CEO' && (
                    <button 
                        onClick={() => setShowForm(!showForm)}
                        className="bg-white/5 hover:bg-[#F57C00] text-white px-5 py-2 rounded-xl text-[9px] font-black flex items-center gap-2 transition-all border border-white/5 active:scale-95"
                    >
                        <PlusCircle size={14} /> {showForm ? "إغلاق" : "تعميد ابتكار جديد @CEO"}
                    </button>
                )}
            </div>

            {/* نموذج الإضافة: محمي برمجياً وبصرياً */}
            {showForm && userRole === 'CEO' && (
                <form onSubmit={handleAdd} className="bg-black/40 border border-white/5 p-5 rounded-3xl grid grid-cols-1 md:grid-cols-4 gap-3 animate-in fade-in zoom-in duration-300">
                    <input 
                        type="text" 
                        placeholder="رقم البراءة (HGR-XXX)" 
                        className="bg-black/60 border border-white/10 p-3 rounded-xl text-[10px] text-white outline-none font-bold" 
                        value={newPatent.patent_id}
                        onChange={e => setNewPatent({...newPatent, patent_id: e.target.value})} 
                        required 
                    />
                    <input 
                        type="text" 
                        placeholder="عنوان الابتكار..." 
                        className="bg-black/60 border border-white/10 p-3 rounded-xl text-[10px] text-white outline-none font-bold" 
                        value={newPatent.title_ar}
                        onChange={e => setNewPatent({...newPatent, title_ar: e.target.value})} 
                        required 
                    />
                    <select 
                        className="bg-black/60 border border-white/10 p-3 rounded-xl text-[10px] text-slate-400 outline-none font-bold"
                        value={newPatent.field}
                        onChange={e => setNewPatent({...newPatent, field: e.target.value})}
                    >
                        <option value="STEAM">STEAM</option>
                        <option value="Robotics">الروبوتكس</option>
                        <option value="AI">الذكاء الاصطناعي</option>
                        <option value="Tools">وسائل تعليمية</option>
                    </select>
                    <button type="submit" className="bg-[#F57C00] text-white font-black rounded-xl text-[9px] uppercase shadow-lg hover:bg-orange-600 transition-colors">
                        تعميد سحابي
                    </button>
                </form>
            )}

            {/* جدول عرض الأصول */}
            <div className="bg-black/20 border border-white/5 rounded-[2rem] overflow-hidden">
                <table className="w-full text-right border-collapse">
                    <thead className="bg-white/5 text-[9px] text-slate-500 uppercase tracking-[0.2em] italic">
                        <tr>
                            <th className="p-4 pr-8">الرقم السيادي</th>
                            <th className="p-4">اسم الابتكار</th>
                            <th className="p-4">المجال</th>
                            {userRole === 'CEO' && <th className="p-4 text-center">الإجراء</th>}
                        </tr>
                    </thead>
                    <tbody className="text-[11px]">
                        {patents.length > 0 ? patents.map(p => (
                            <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-all group">
                                <td className="p-4 pr-8 font-mono text-[#F57C00] font-bold">{p.patent_id}</td>
                                <td className="p-4 font-bold text-slate-300">{p.title_ar}</td>
                                <td className="p-4">
                                    <span className="bg-blue-500/5 text-blue-400 border border-blue-500/10 px-3 py-0.5 rounded-lg text-[9px] font-black uppercase">
                                        {p.field}
                                    </span>
                                </td>
                                {userRole === 'CEO' && (
                                    <td className="p-4 text-center">
                                        <button onClick={() => onApprove && onApprove(p.title_ar)} className="p-2 hover:bg-[#F57C00]/10 rounded-lg group/btn">
                                            <Zap size={14} className="text-slate-600 group-hover/btn:text-[#F57C00] transition-colors" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={userRole === 'CEO' ? 4 : 3} className="p-12 text-center text-slate-600 italic text-[10px]">
                                    بانتظار مزامنة الأصول المعرفية من الخادم الرئيسي...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PatentsTable;